/**
 * ValidationTagBadge - small visible chip that surfaces a UAE-overlay
 * validation tag next to an NBA title or citation row (Wave 5 Phase A).
 *
 * Wave 4 P2 evaluation (E6) flagged that validation tags exist in the
 * data layer (UAE-overlay pathways, NBA candidate metadata) but no
 * rendering pathway surfaces them, so clinicians cannot see uncertainty
 * markers. This component is the rendering primitive that closes that
 * gap.
 *
 * Tag vocabulary is the closed set defined in `src/types/uae-overlay.ts`:
 *   - [IMPLEMENTED]         - low friction, evidence-backed
 *   - [UNVERIFIED]          - visible warning tone
 *   - [CONTEXTUAL EVIDENCE] - informational, distinguishable from warning
 *   - [PLANNED]             - muted, future-scope
 *
 * HC2: every color, spacing, radius value resolves through Signal DS
 * tokens (no raw hex). HC5: zero em / en dashes anywhere.
 *
 * HC6 a11y: badge carries `aria-label` so SR users hear the tag name as
 * a sentence ("validation status, IMPLEMENTED") rather than the bracketed
 * literal.
 *
 * HC1 reproducibility: pure presentation - no clocks, no randomness.
 *
 * HC9 telemetry-real: this badge does not emit telemetry. UAE-overlay
 * telemetry binding is Wave 5 Phase D and is intentionally out of scope.
 */
import type { ValidationTag } from '../types/uae-overlay';

export interface ValidationTagBadgeProps {
  /** The validation tag to render. */
  tag: ValidationTag;
  /**
   * Optional anchor id used in the test id so a host that renders many
   * badges (one per NBA, one per citation row) can disambiguate them in
   * tests. Defaults to the tag slug.
   */
  anchorId?: string;
  className?: string;
}

/**
 * Token-driven palette per tag class. Reuses existing Signal DS tokens
 * so no new color tokens are introduced (Wave 5 Phase A constraint).
 *
 * - IMPLEMENTED         -> status-active (green), low friction
 * - UNVERIFIED          -> warning-strong (red-orange), visible warning
 * - CONTEXTUAL EVIDENCE -> signal-primary (brand blue), informational
 * - PLANNED             -> surface-muted + text-tertiary, muted future tone
 */
const TAG_CLASSES: Record<ValidationTag, string> = {
  '[IMPLEMENTED]':
    'bg-status-active-bg text-status-active-fg ring-status-active',
  '[UNVERIFIED]':
    'bg-warning-strong-bg text-warning-strong-fg ring-warning-strong-border',
  '[CONTEXTUAL EVIDENCE]':
    'bg-signal-primary-100 text-signal-primary-700 ring-signal-primary-300',
  '[PLANNED]': 'bg-surface-muted text-text-tertiary ring-surface-border',
};

/**
 * Stable slug per tag for test ids and anchor ids. The slug is the tag
 * minus the brackets, lowercased, with spaces collapsed to dashes.
 */
const TAG_SLUG: Record<ValidationTag, string> = {
  '[IMPLEMENTED]': 'implemented',
  '[UNVERIFIED]': 'unverified',
  '[CONTEXTUAL EVIDENCE]': 'contextual-evidence',
  '[PLANNED]': 'planned',
};

/**
 * Visible label rendered inside the badge. The brackets are dropped
 * because the chip shape itself signals "tag", but the `aria-label`
 * keeps the canonical wording for SR users.
 */
const TAG_VISIBLE_LABEL: Record<ValidationTag, string> = {
  '[IMPLEMENTED]': 'IMPLEMENTED',
  '[UNVERIFIED]': 'UNVERIFIED',
  '[CONTEXTUAL EVIDENCE]': 'CONTEXTUAL EVIDENCE',
  '[PLANNED]': 'PLANNED',
};

export function ValidationTagBadge({
  tag,
  anchorId,
  className,
}: ValidationTagBadgeProps) {
  const slug = TAG_SLUG[tag];
  const testId = `validation-tag-badge-${anchorId ?? slug}`;
  const label = TAG_VISIBLE_LABEL[tag];
  const ariaLabel = `Validation status, ${label}`;

  const baseClasses =
    'inline-flex w-fit items-center gap-1 rounded-signal-pill px-gutter-sm py-0.5 text-signal-xs font-bold uppercase tracking-wider ring-1 ring-inset';
  const composed = [baseClasses, TAG_CLASSES[tag], className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <span
      data-testid={testId}
      data-validation-tag={slug}
      aria-label={ariaLabel}
      title={label}
      className={composed}
    >
      {label}
    </span>
  );
}

export { TAG_CLASSES, TAG_SLUG, TAG_VISIBLE_LABEL };
